/* eslint-disable react-refresh/only-export-components */
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
  faCheck,
  faCloudArrowUp,
  faFile,
  faFileCircleExclamation,
} from "@fortawesome/pro-solid-svg-icons"
import { faSpinner, faXmark } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { cva } from "class-variance-authority"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

import { InlineAlert } from "../inline-alert/inline-alert"

export type FileUploaderProps = Omit<
  ComponentProps<"input">,
  "type" | "className"
> & {
  className?: string
  /** Text in der Dropzone, Figma: "Klicken zum Hochladen". */
  label?: string
  /** Liste der `FileUploaderItem`s, wird unterhalb der Dropzone gerendert. */
  children?: ReactNode
}

function FileUploader({
  className,
  label = "Klicken zum Hochladen",
  children,
  ...inputProps
}: FileUploaderProps) {
  return (
    <div
      data-slot="file-uploader"
      className={cn("flex w-full flex-col items-start gap-3", className)}
    >
      <label
        data-slot="file-uploader-dropzone"
        className="flex w-full cursor-pointer flex-col items-center gap-3 rounded-xl border border-dashed border-neutrals-v2-400 p-5"
      >
        <input type="file" className="sr-only" {...inputProps} />
        {/* Figma: rounded-[10.8px] (Rundungsartefakt) — auf rounded-lg
            normalisiert, analog zum Icon-Container in FileUploaderItem. */}
        <div className="flex size-9 items-center justify-center rounded-lg bg-neutrals-v2-200">
          <FontAwesomeIcon
            icon={faCloudArrowUp}
            className="size-5.5 text-brand-red-200"
          />
        </div>
        <p className="text-center text-sm leading-5 text-labels-primary">
          {label}
        </p>
      </label>
      {children && (
        <div
          data-slot="file-uploader-list"
          className="flex w-full flex-col items-start gap-2"
        >
          {children}
        </div>
      )}
    </div>
  )
}

const ROW_STATES = ["loading", "success", "uploaded"] as const

const fileUploaderItemVariants = cva(
  "group/file-uploader-item relative flex w-full",
  {
    variants: {
      state: {
        loading: "",
        success: "",
        uploaded: "",
        uploadFailed: "flex-col items-start gap-1",
      },
    },
    compoundVariants: [
      {
        state: [...ROW_STATES],
        class: "items-center gap-2 rounded-xl bg-neutrals-v2-200 p-3",
      },
    ],
  }
)

type FileUploaderItemBaseProps = {
  className?: string
  /** Dateiname, wird bei Überlänge abgeschnitten dargestellt. */
  fileName: string
}

export type FileUploaderItemLoadingProps = FileUploaderItemBaseProps & {
  state: "loading"
  /** Bild-URL für die Vorschau; ohne Vorschau erscheint ein generisches Datei-Icon. */
  thumbnail?: string
}

export type FileUploaderItemSuccessProps = FileUploaderItemBaseProps & {
  state: "success"
  thumbnail?: string
}

export type FileUploaderItemUploadedProps = FileUploaderItemBaseProps & {
  state: "uploaded"
  thumbnail?: string
  onRemove?: () => void
}

export type FileUploaderItemUploadFailedProps = FileUploaderItemBaseProps & {
  state: "uploadFailed"
  /** Figma: "Datei zu groß. Maximal 10 MB erlaubt." — als freier Text übernommen. */
  errorMessage: string
  onRemove?: () => void
}

export type FileUploaderItemProps =
  | FileUploaderItemLoadingProps
  | FileUploaderItemSuccessProps
  | FileUploaderItemUploadedProps
  | FileUploaderItemUploadFailedProps

function FileThumbnail({
  thumbnail,
  icon = faFile,
}: {
  thumbnail?: string
  icon?: IconDefinition
}) {
  if (thumbnail) {
    return (
      <img
        src={thumbnail}
        alt=""
        className="size-9 shrink-0 rounded-lg object-cover"
      />
    )
  }
  return (
    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutrals-v2-300">
      <FontAwesomeIcon icon={icon} className="size-5.5 text-labels-primary" />
    </div>
  )
}

function FileInfo({
  fileName,
  thumbnail,
  icon,
}: {
  fileName: string
  thumbnail?: string
  icon?: IconDefinition
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <FileThumbnail thumbnail={thumbnail} icon={icon} />
      <p className="min-w-0 flex-1 truncate text-sm leading-5 text-labels-primary">
        {fileName}
      </p>
    </div>
  )
}

function RemoveButton({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      data-slot="file-uploader-item-remove"
      onClick={onClick}
      className="flex size-8 shrink-0 items-center justify-center rounded-full p-2 text-neutrals-v2-700 outline-none hover:bg-neutrals-v2-300 focus-visible:ring-2 focus-visible:ring-neutrals-v2-600 focus-visible:ring-offset-2"
    >
      <FontAwesomeIcon icon={faXmark} className="size-4.5" />
      <span className="sr-only">Datei entfernen</span>
    </button>
  )
}

function FileUploaderItem(props: FileUploaderItemProps) {
  const { className, fileName, state } = props

  return (
    <div
      data-slot="file-uploader-item"
      data-state={state}
      className={cn(fileUploaderItemVariants({ state, className }))}
    >
      {props.state === "uploadFailed" ? (
        <>
          <div className="flex h-14 w-full items-center gap-2 rounded-xl border-2 border-error-red-200 bg-neutrals-v2-200 p-3">
            <FileInfo fileName={fileName} icon={faFileCircleExclamation} />
            <RemoveButton onClick={props.onRemove} />
          </div>
          <InlineAlert description={props.errorMessage} />
        </>
      ) : (
        <>
          <FileInfo fileName={fileName} thumbnail={props.thumbnail} />
          {props.state === "loading" && (
            <div className="flex size-8 shrink-0 items-center justify-center">
              <FontAwesomeIcon
                icon={faSpinner}
                className="size-5.5 animate-spin text-neutrals-v2-500"
              />
            </div>
          )}
          {props.state === "success" && (
            <div className="flex size-8 shrink-0 items-center justify-center">
              <FontAwesomeIcon
                icon={faCheck}
                className="size-5 text-accent-green-100"
              />
            </div>
          )}
          {props.state === "uploaded" && (
            <RemoveButton onClick={props.onRemove} />
          )}
        </>
      )}
    </div>
  )
}

export { FileUploader, FileUploaderItem, fileUploaderItemVariants }
